import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ScanHistory = ({ history, onClearHistory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!history || history?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground">Recent Scans</h3>
          <span className="text-xs text-muted-foreground">0 scans</span>
        </div>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="History" size={24} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">No scans yet</p>
        </div>
      </div>
    );
  }

  const displayedHistory = isExpanded ? history : history?.slice(0, 3);

  const getStatusIcon = (result) => {
    if (!result?.isValid) return { name: 'XCircle', color: 'text-error' };
    
    switch (result?.type) {
      case 'entry':
        return { name: 'LogIn', color: 'text-success' };
      case 'upgrade':
        return { name: 'ArrowUp', color: 'text-accent' };
      case 'merchandise':
        return { name: 'Package', color: 'text-primary' };
      case 'member':
        return { name: 'Star', color: 'text-warning' };
      default:
        return { name: 'CheckCircle', color: 'text-success' };
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'entry':
        return 'Entry';
      case 'upgrade':
        return 'Upgrade';
      case 'merchandise':
        return 'Merchandise';
      case 'member':
        return 'Member';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="History" size={18} className="text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Recent Scans</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground">
            {history?.length} scan{history?.length !== 1 ? 's' : ''}
          </span>
          {history?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              onClick={onClearHistory}
              className="text-muted-foreground hover:text-error"
            />
          )}
        </div>
      </div>
      {/* History List */}
      <div className="divide-y divide-border">
        {displayedHistory?.map((scan, index) => {
          const statusIcon = getStatusIcon(scan);
          
          return (
            <div key={scan?.id || index} className="p-4 hover:bg-muted transition-colors">
              <div className="flex items-start space-x-3">
                {/* Status Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  scan?.isValid ? 'bg-success bg-opacity-10' : 'bg-error bg-opacity-10'
                }`}>
                  <Icon name={statusIcon?.name} size={16} className={statusIcon?.color} />
                </div>
                
                {/* Scan Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {scan?.data?.attendeeName || 'Unknown Attendee'}
                    </p>
                    <span className="text-xs text-muted-foreground ml-2">
                      {scan?.timestamp?.toLocaleTimeString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Hash" size={12} />
                      <span className="font-mono">{scan?.id}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Tag" size={12} />
                      <span>{getTypeLabel(scan?.type)}</span>
                    </span>
                    <span className={`font-medium ${scan?.isValid ? 'text-success' : 'text-error'}`}>
                      {scan?.isValid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                  
                  {scan?.data?.eventName && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {scan?.data?.eventName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Expand/Collapse Button */}
      {history?.length > 3 && (
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            className="w-full text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? 'Show Less' : `Show ${history?.length - 3} More`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScanHistory;